import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@monksflow/monks-ui';
import {
  MaximizeIcon,
  MinimizeIcon,
  PauseIcon,
  PlayIcon,
  Volume2Icon,
  VolumeXIcon,
} from 'lucide-react';

import type { Html5PlayerProps, VendorDocumentFS, VendorElementFS } from './Html5Player.types';
import { useTranslation } from 'react-i18next';

const addVendorFsListener = (doc: Document, type: string, handler: () => void) => {
  (
    doc as unknown as {
      addEventListener: (
        t: string,
        h: () => void,
        opts?: boolean | AddEventListenerOptions,
      ) => void;
    }
  ).addEventListener(type, handler);
};
const removeVendorFsListener = (doc: Document, type: string, handler: () => void) => {
  (
    doc as unknown as {
      removeEventListener: (
        t: string,
        h: () => void,
        opts?: boolean | EventListenerOptions,
      ) => void;
    }
  ).removeEventListener(type, handler);
};

/**
 * The viewport less the side panel's chrome above the player, so a portrait video's controls stay
 * on screen instead of sitting a scroll below its native height.
 */
const VIEWPORT_CAPPED_HEIGHT = 'max-h-[calc(100dvh-9rem)]';

export function Html5Player({ src, type }: Html5PlayerProps) {
  const { t } = useTranslation('contentTest');

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [duration, setDuration] = useState(0);
  const [time, setTime] = useState(0);
  const [isFs, setIsFs] = useState(false);
  const [isVerticalVideo, setIsVerticalVideo] = useState(false);

  const fmt = useCallback((sec: number) => {
    if (!Number.isFinite(sec)) return '0:00';
    const s = Math.max(0, Math.floor(sec));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, '0')}`;
  }, []);

  const progressPct = useMemo(() => (duration > 0 ? (time / duration) * 100 : 0), [time, duration]);

  const timelineBg = useMemo<React.CSSProperties>(
    () => ({
      background: `linear-gradient(to right, #ffffff ${progressPct}%, rgba(255,255,255,.35) ${progressPct}%)`,
    }),
    [progressPct],
  );

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onLoaded = () => {
      setDuration(v.duration || 0);
      if (v.videoWidth && v.videoHeight) {
        setIsVerticalVideo(v.videoHeight > v.videoWidth);
      }
    };
    const onTime = () => setTime(v.currentTime || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onVol = () => {
      setMuted(v.muted);
      setVolume(v.volume);
    };

    v.addEventListener('loadedmetadata', onLoaded);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('volumechange', onVol);

    return () => {
      v.removeEventListener('loadedmetadata', onLoaded);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('volumechange', onVol);
    };
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      const d = document as VendorDocumentFS;
      const active =
        !!document.fullscreenElement || !!d.webkitFullscreenElement || !!d.msFullscreenElement;
      setIsFs(active);
    };

    document.addEventListener('fullscreenchange', onFsChange);
    addVendorFsListener(document, 'webkitfullscreenchange', onFsChange);
    addVendorFsListener(document, 'MSFullscreenChange', onFsChange);

    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      removeVendorFsListener(document, 'webkitfullscreenchange', onFsChange);
      removeVendorFsListener(document, 'MSFullscreenChange', onFsChange);
    };
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) void v.play();
    else v.pause();
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
  };

  const onSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = videoRef.current;
    if (!v) return;
    const val = Number(e.target.value);
    v.currentTime = val;
    setTime(val);
  };

  const enterFs = () => {
    const el = wrapRef.current as VendorElementFS | null;
    if (!el) return;

    if (typeof el.requestFullscreen === 'function') {
      void el.requestFullscreen();
    } else if (typeof el.webkitRequestFullscreen === 'function') {
      void el.webkitRequestFullscreen();
    } else if (typeof el.msRequestFullscreen === 'function') {
      void el.msRequestFullscreen();
    }
  };

  const exitFs = () => {
    const d = document as VendorDocumentFS;

    if ('exitFullscreen' in document && typeof document.exitFullscreen === 'function') {
      void document.exitFullscreen();
    } else if (typeof d.webkitExitFullscreen === 'function') {
      void d.webkitExitFullscreen();
    } else if (typeof d.msExitFullscreen === 'function') {
      void d.msExitFullscreen();
    }
  };

  return (
    <div ref={wrapRef} className="relative rounded-2xl bg-black">
      <video
        ref={videoRef}
        data-testid="html5-player-video"
        playsInline
        preload="metadata"
        className={`${isFs ? 'max-h-dvh' : VIEWPORT_CAPPED_HEIGHT} ${
          isVerticalVideo
            ? 'block mx-auto my-0 h-auto w-auto max-w-full'
            : 'block w-full h-auto bg-black rounded-2xl'
        }`}
        controls={false}
      >
        <source src={src} type={type} />
        <track
          kind="captions"
          srcLang="en"
          label="English"
          src="data:text/vtt;base64,V0VCVlRUCg=="
        />
      </video>
      {/* The chrome sits over an arbitrary video frame, so its colours are fixed
          rather than themed — the same reason `.ie-pill` below hardcodes its scrim. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 p-4">
        <input
          type="range"
          min={0}
          max={Math.max(0, duration)}
          step="0.01"
          value={time}
          onChange={onSeek}
          aria-label={t('videoWidget.html5Player.seek')}
          className="ie-video-slider pointer-events-auto w-full cursor-grab"
          style={timelineBg}
        />
        <div className="pointer-events-auto mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="ie-pill">
              <Button
                variant="ghost"
                size="icon-sm"
                roundness="full"
                onClick={togglePlay}
                className="text-white"
                aria-label={
                  isPlaying ? t('videoWidget.html5Player.pause') : t('videoWidget.html5Player.play')
                }
              >
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
              </Button>
            </div>
            <div className="ie-pill">
              <Button
                variant="ghost"
                size="icon-sm"
                roundness="full"
                onClick={toggleMute}
                className="text-white"
                aria-label={
                  muted || volume === 0
                    ? t('videoWidget.html5Player.unmute')
                    : t('videoWidget.html5Player.mute')
                }
              >
                {muted || volume === 0 ? <VolumeXIcon /> : <Volume2Icon />}
              </Button>
            </div>
            <div className="ie-pill">
              <span className="px-3 text-body-md text-white">
                {fmt(time)} / {fmt(duration)}
              </span>
            </div>
          </div>
          <div className="ie-pill">
            <Button
              variant="ghost"
              size="icon-sm"
              roundness="full"
              onClick={isFs ? exitFs : enterFs}
              className="text-white"
              aria-label={
                isFs
                  ? t('videoWidget.html5Player.exitFullscreen')
                  : t('videoWidget.html5Player.enterFullscreen')
              }
            >
              {isFs ? <MinimizeIcon /> : <MaximizeIcon />}
            </Button>
          </div>
        </div>
      </div>
      <style>{`
        .ie-video-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          border-radius: 9999px;
          outline: none;
          width: 100%;
        }
        .ie-video-slider::-webkit-slider-runnable-track {
          height: 4px;
          border-radius: 9999px;
          background: transparent;
        }
        .ie-video-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 0 0 2px rgba(0,0,0,.2);
          margin-top: -5px; /* centers thumb on 4px track */
          cursor: grab;
        }
        .ie-video-slider:active::-webkit-slider-thumb {
          cursor: grabbing;
        }
        /* Firefox */
        .ie-video-slider::-moz-range-track {
          height: 4px;
          border-radius: 9999px;
          background: transparent;
        }
        .ie-video-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #fff;
          border: none;
          box-shadow: 0 0 0 2px rgba(0,0,0,.2);
          cursor: grab;
        }
        .ie-video-slider:active::-moz-range-thumb {
          cursor: grabbing;
        }

        .ie-pill {
          display: inline-flex;
          align-items: center;
          background: rgba(0,0,0,.25);
          border-radius: 9999px;
          height: 36px;
        }
      `}</style>
    </div>
  );
}
